import React, { useState, useEffect } from 'react';
import { Fab, Fade, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { KeyboardArrowUp as UpIcon } from '@mui/icons-material';

/**
 * Back-to-top button, bottom right, shown only once there is something to scroll back from.
 *
 * The window is the scroller here, not `<main>` — the shell sets `overflowX: hidden` but
 * leaves vertical scrolling to the page, which is why the pagination handlers elsewhere all
 * call `window.scrollTo`.
 *
 * Hidden entirely at the top rather than disabled: a permanent button on a short page is
 * clutter over content, and on a phone it would sit on top of whatever is in that corner.
 */
export const ScrollToTop: React.FC = () => {
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('sm'));
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Roughly one viewport of scrolling before it appears: enough that "back to top" is a
    // real journey rather than a nudge the scrollbar already handles.
    const threshold = Math.max(400, window.innerHeight * 0.75);

    let frame = 0;
    const onScroll = () => {
      // Coalesce to one check per frame — scroll fires far more often than this needs to run.
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        setShow(window.scrollY > threshold);
      });
    };

    onScroll();   // a reload partway down a page should show it immediately
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <Fade in={show}>
      <Tooltip title="Back to top" placement="left" arrow>
        <Fab
          size={narrow ? 'small' : 'medium'}
          color="primary"
          aria-label="Back to top"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          sx={{
            position: 'fixed',
            // Clear of the iOS home indicator and any browser chrome at the bottom.
            bottom: { xs: 'calc(16px + env(safe-area-inset-bottom))', sm: 24 },
            right: { xs: 16, sm: 24 },
            zIndex: theme.zIndex.speedDial,
            // Not interactive while invisible — Fade leaves the node in the DOM, and an
            // unreachable-looking button that still swallows clicks is worse than none.
            pointerEvents: show ? 'auto' : 'none',
          }}
        >
          <UpIcon />
        </Fab>
      </Tooltip>
    </Fade>
  );
};
